'use client'

import * as React from 'react'

import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { PromptInputProvider } from './PromptInputContext'

interface PromptInputProps {
  isLoading?: boolean
  value?: string
  onValueChange?: (value: string) => void
  maxHeight?: number | string
  onSubmit?: () => void
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onDragOver?: (event: React.DragEvent) => void
  onDragLeave?: (event: React.DragEvent) => void
  onDrop?: (event: React.DragEvent) => void
}

const PromptInput = React.forwardRef<HTMLDivElement, PromptInputProps>(
  (
    {
      className,
      isLoading = false,
      maxHeight = 240,
      value,
      onValueChange,
      onSubmit,
      children,
      disabled = false,
      onDragOver,
      onDragLeave,
      onDrop
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(value ?? '')

    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value)
      }
    }, [value])

    const handleChange = React.useCallback(
      (newValue: string) => {
        setInternalValue(newValue)
        onValueChange?.(newValue)
      },
      [onValueChange]
    )

    return (
      <TooltipProvider>
        <PromptInputProvider
          value={{
            isLoading,
            value: value ?? internalValue,
            setValue: onValueChange ?? handleChange,
            maxHeight,
            onSubmit,
            disabled
          }}
        >
          <div
            ref={ref}
            className={cn(
              'rounded-3xl bg-white/90 backdrop-blur-md p-2 shadow-[0_20px_40px_rgba(255,255,255,0.3),inset_0_1px_0_rgba(255,255,255,0.5),0_0_20px_rgba(255,255,255,0.4)] transition-all duration-300',
              isLoading && 'border-red-500/50',
              className
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {children}
          </div>
        </PromptInputProvider>
      </TooltipProvider>
    )
  }
)

PromptInput.displayName = 'PromptInput'

export type { PromptInputProps }
export { PromptInput }
